'use client';

import { useMemo, type ReactNode } from 'react';
import AssetApyBanner from './AssetApyBanner';
// TODO: Uncomment for USD1 campaign launch
// import USD1CampaignBanner from './USD1CampaignBanner';
import { useMarketInterestData } from 'src/hooks/earn/useMarketInterestData';
import {
	USDC_SPOT_MARKET_INDEX,
	SOL_SPOT_MARKET_INDEX,
	WBTC_SPOT_MARKET_INDEX,
	WETH_SPOT_MARKET_INDEX,
	CBBTC_SPOT_MARKET_INDEX,
} from '@velocity-exchange/common';
import StepIndicator from 'src/components/primitives/StepIndicator/StepIndicator';
import useBannerCarousel from 'src/components/ApyBanners/useBannerCarousel';
import { cm } from 'src/utils/classNames';

const THRESHOLDS: Record<string, number> = {
	SOL: 10,
	wETH: 10,
	wBTC: 5,
	cbBTC: 5,
};

interface BannerItem {
	key: string;
	element: ReactNode;
}

const DEFAULT_BANNERS: BannerItem[] = [
	{
		key: 'USDC',
		element: (
			<AssetApyBanner
				marketIndex={USDC_SPOT_MARKET_INDEX}
				assetLabel="USDC"
				baseSymbolForIcon="usdc"
			/>
		),
	},
	// TODO: Uncomment for USD1 campaign launch
	// {
	// 	key: 'USD1Campaign',
	// 	element: <USD1CampaignBanner />,
	// },
];

function ApyBanners() {
	const solData = useMarketInterestData({ marketIndex: SOL_SPOT_MARKET_INDEX });
	const wbtcData = useMarketInterestData({
		marketIndex: WBTC_SPOT_MARKET_INDEX,
	});
	const wethData = useMarketInterestData({
		marketIndex: WETH_SPOT_MARKET_INDEX,
	});
	const cbbtcData = useMarketInterestData({
		marketIndex: CBBTC_SPOT_MARKET_INDEX,
	});

	const conditionalBanners: BannerItem[] = useMemo(() => {
		const items: BannerItem[] = [];
		if (solData && solData.deposit.totalApy >= THRESHOLDS.SOL) {
			items.push({
				key: 'SOL',
				element: (
					<AssetApyBanner
						marketIndex={SOL_SPOT_MARKET_INDEX}
						assetLabel="SOL"
						baseSymbolForIcon="sol"
					/>
				),
			});
		}
		if (wethData && wethData.deposit.totalApy >= THRESHOLDS.wETH) {
			items.push({
				key: 'wETH',
				element: (
					<AssetApyBanner
						marketIndex={WETH_SPOT_MARKET_INDEX}
						assetLabel="wETH"
						baseSymbolForIcon="weth"
						bodyLabel="ETH"
					/>
				),
			});
		}
		if (wbtcData && wbtcData.deposit.totalApy >= THRESHOLDS.wBTC) {
			items.push({
				key: 'wBTC',
				element: (
					<AssetApyBanner
						marketIndex={WBTC_SPOT_MARKET_INDEX}
						assetLabel="wBTC"
						baseSymbolForIcon="wbtc"
						bodyLabel="BTC"
					/>
				),
			});
		}
		if (cbbtcData && cbbtcData.deposit.totalApy >= THRESHOLDS.cbBTC) {
			items.push({
				key: 'cbBTC',
				element: (
					<AssetApyBanner
						marketIndex={CBBTC_SPOT_MARKET_INDEX}
						assetLabel="cbBTC"
						baseSymbolForIcon="cbbtc"
					/>
				),
			});
		}
		return items;
	}, [solData, wbtcData, wethData, cbbtcData]);

	const allBanners = useMemo(
		() => [...DEFAULT_BANNERS, ...conditionalBanners],
		[conditionalBanners]
	);

	const numBanners = allBanners.length;

	const { activeIdx, handleMouseEnter, handleMouseLeave, handleStepClick } =
		useBannerCarousel({
			numBanners,
			rotationIntervalMs: 10000,
		});

	const activeBanner = allBanners[activeIdx];

	if (numBanners === 0 || !activeBanner) {
		return null;
	}

	if (numBanners === 1) {
		return activeBanner.element;
	}

	return (
		<div className="flex flex-col gap-y-sm">
			<div
				className="relative"
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{allBanners.map((banner, index) => (
					<div
						key={banner.key}
						className={cm(
							'transition-opacity duration-500',
							index === activeIdx ? 'opacity-100' : 'opacity-0',
							index !== activeIdx && 'pointer-events-none absolute inset-0'
						)}
					>
						{banner.element}
					</div>
				))}
			</div>

			<StepIndicator
				className="w-fit self-center"
				currentStep={activeIdx}
				totalSteps={numBanners}
				onClick={handleStepClick}
			/>
		</div>
	);
}

export default ApyBanners;
